import React from "react";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface DataTablePaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange?: (itemsPerPage: number) => void;
  itemsPerPageOptions?: number[];
  siblingCount?: number;
  showItemsPerPageSelector?: boolean;
  showTotalItems?: boolean;
  className?: string;
}

const DataTablePagination: React.FC<DataTablePaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  itemsPerPageOptions = [10, 25, 50, 100],
  siblingCount = 1,
  showItemsPerPageSelector = true,
  showTotalItems = true,
  className,
}) => {
  const range = (start: number, end: number) => {
    const length = end - start + 1;
    return Array.from({ length }, (_, idx) => idx + start);
  };

  const getPaginationRange = () => {
    const totalPageNumbers = siblingCount * 2 + 5; // Current page + 2 siblings + first + last + 2 ellipsis

    if (totalPages <= totalPageNumbers) {
      return range(1, totalPages);
    }

    const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
    const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages);

    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < totalPages - 1;

    if (!shouldShowLeftDots && shouldShowRightDots) {
      let leftItemCount = 3 + 2 * siblingCount;
      return [...range(1, leftItemCount), '...', totalPages];
    }

    if (shouldShowLeftDots && !shouldShowRightDots) {
      let rightItemCount = 3 + 2 * siblingCount;
      return [1, '...', ...range(totalPages - rightItemCount + 1, totalPages)];
    }

    if (shouldShowLeftDots && shouldShowRightDots) {
      const middleRange = range(leftSiblingIndex, rightSiblingIndex);
      return [1, '...', ...middleRange, '...', totalPages];
    }

    return range(1, totalPages);
  };

  const paginationRange = getPaginationRange();

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      onPageChange(page);
    }
  };

  const handleEllipsisClick = (direction: 'left' | 'right') => {
    if (direction === 'left') {
      const targetPage = Math.max(1, currentPage - siblingCount * 3);
      handlePageChange(targetPage);
    } else {
      const targetPage = Math.min(totalPages, currentPage + siblingCount * 3);
      handlePageChange(targetPage);
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage: string) => {
    const items = parseInt(newItemsPerPage);
    if (onItemsPerPageChange) {
      onItemsPerPageChange(items);
    }
  };

  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  // if (totalPages <= 1) {
  //   return null;
  // }

  return (
    <div
      className={`flex flex-col sm:flex-row items-center justify-between gap-4 px-2 ${className}`}
    >
      {/* Items per page selector and items count */}
      <div className="flex items-center gap-4 text-sm text-muted-foreground">
        {showTotalItems && (
          <div>
            Showing {startItem} to {endItem} of {totalItems} items
          </div>
        )}

        {showItemsPerPageSelector && onItemsPerPageChange && (
          <div className="flex items-center gap-2">
            <span>Rows per page:</span>
            <Select
              value={itemsPerPage.toString()}
              onValueChange={handleItemsPerPageChange}
            >
              <SelectTrigger className="h-8 w-16">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {itemsPerPageOptions.map((option) => (
                  <SelectItem key={option} value={option.toString()}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Pagination controls */}
      <div className="flex items-center gap-1">
        {/* First page button */}
        <Button
          variant="outline"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => handlePageChange(1)}
          disabled={currentPage === 1}
        >
          <ChevronsLeft className="h-4 w-4" />
        </Button>

        {/* Previous page button */}
        <Button
          variant="outline"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        {/* Page numbers and clickable ellipsis */}
        <div className="flex items-center gap-1">
          {paginationRange.map((pageNumber, index) => {
            if (pageNumber === '...') {
              // Determine if this is left or right ellipsis
              const isLeftEllipsis = index === 1;
              const targetPage = isLeftEllipsis
                ? Math.max(1, currentPage - siblingCount * 3)
                : Math.min(totalPages, currentPage + siblingCount * 3);
              const tooltipText = isLeftEllipsis
                ? `Jump back to page ${targetPage}`
                : `Jump forward to page ${targetPage}`;

              return (
                <Button
                  key={`ellipsis-${index}`}
                  variant="outline"
                  size="sm"
                  className="h-8 w-12 p-0 hover:bg-primary hover:text-primary-foreground transition-colors border-dashed"
                  onClick={() => handleEllipsisClick(isLeftEllipsis ? 'left' : 'right')}
                  title={tooltipText}
                  aria-label={tooltipText}
                >
                  <span className="text-xs font-medium">...</span>
                </Button>
              );
            }

            const pageNum = pageNumber as number;
            return (
              <Button
                key={pageNum}
                variant={currentPage === pageNum ? "default" : "outline"}
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => handlePageChange(pageNum)}
              >
                {pageNum}
              </Button>
            );
          })}
        </div>

        {/* Next page button */}
        <Button
          variant="outline"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>

        {/* Last page button */}
        <Button
          variant="outline"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage === totalPages}
        >
          <ChevronsRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default DataTablePagination;